import React from 'react';
import { motion } from 'framer-motion';

/**
 * HeroVisual — an elegant inline SVG illustration tuned to the Bogin Tech
 * brand palette (navy #0B1957, orange #ff6a00 / #d44a00, blue #1438bc).
 * Composition: a floating glass dashboard panel with a code window,
 * an analytics card, an AI badge, and subtle ambient elements — all crisp
 * vector, fully responsive, with parallax-style framer-motion animations.
 */
const HeroVisual = () => {
    return (
        <motion.div
            className="bt-hero__visual-wrap"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            aria-hidden="true"
        >
            <motion.svg
                viewBox="0 0 620 660"
                xmlns="http://www.w3.org/2000/svg"
                overflow="visible"
                className="bt-hero__svg"
                role="img"
                aria-label="Bogin Tech custom software development illustration"
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.25 }}
            >
                <defs>
                    {/* Gradients */}
                    <linearGradient id="bt-grad-navy" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#11227a" />
                        <stop offset="100%" stopColor="#0B1957" />
                    </linearGradient>
                    <linearGradient id="bt-grad-warm" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#ff8a3d" />
                        <stop offset="100%" stopColor="#d44a00" />
                    </linearGradient>
                    <linearGradient id="bt-grad-blue" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#2a55e8" />
                        <stop offset="100%" stopColor="#1438bc" />
                    </linearGradient>
                    <linearGradient id="bt-grad-glass" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
                        <stop offset="100%" stopColor="#f5f7ff" stopOpacity="0.85" />
                    </linearGradient>
                    <linearGradient id="bt-line" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#ff6a00" stopOpacity="0" />
                        <stop offset="50%" stopColor="#ff6a00" stopOpacity="0.9" />
                        <stop offset="100%" stopColor="#ff6a00" stopOpacity="0" />
                    </linearGradient>

                    {/* Soft shadow filter */}
                    <filter id="bt-shadow" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="14" />
                        <feOffset dx="0" dy="14" result="offsetblur" />
                        <feComponentTransfer>
                            <feFuncA type="linear" slope="0.22" />
                        </feComponentTransfer>
                        <feMerge>
                            <feMergeNode />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>

                    <filter id="bt-shadow-soft" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="8" />
                        <feOffset dx="0" dy="6" result="offsetblur" />
                        <feComponentTransfer>
                            <feFuncA type="linear" slope="0.18" />
                        </feComponentTransfer>
                        <feMerge>
                            <feMergeNode />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                {/* Background ambient blob */}
                <motion.circle
                    cx="495" cy="120" r="120"
                    fill="url(#bt-grad-warm)" opacity="0.18"
                    animate={{ scale: [1, 1.08, 1] }}
                    transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
                />
                <motion.circle
                    cx="80" cy="540" r="140"
                    fill="url(#bt-grad-blue)" opacity="0.14"
                    animate={{ scale: [1, 1.12, 1] }}
                    transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
                />

                {/* Decorative dotted ring */}
                <motion.g
                    style={{ transformOrigin: '500px 540px' }}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 38, repeat: Infinity, ease: 'linear' }}
                >
                    <circle cx="500" cy="540" r="60" fill="none"
                        stroke="#0B1957" strokeWidth="1.4" strokeDasharray="2 8" opacity="0.35" />
                </motion.g>

                {/* Main dashboard panel */}
                <motion.g
                    filter="url(#bt-shadow)"
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                >
                    <rect x="80" y="110" width="430" height="320" rx="22"
                        fill="url(#bt-grad-glass)" stroke="rgba(11,25,87,0.08)" />

                    {/* Window header dots */}
                    <circle cx="106" cy="138" r="6" fill="#ff6a00" />
                    <circle cx="126" cy="138" r="6" fill="#ffb27a" />
                    <circle cx="146" cy="138" r="6" fill="#0B1957" opacity="0.25" />
                    <rect x="180" y="130" width="180" height="16" rx="8" fill="#0B1957" opacity="0.06" />

                    {/* Sidebar */}
                    <rect x="100" y="170" width="92" height="240" rx="12" fill="#0B1957" opacity="0.04" />
                    <rect x="112" y="186" width="68" height="10" rx="5" fill="#0B1957" opacity="0.55" />
                    <rect x="112" y="208" width="48" height="8" rx="4" fill="#0B1957" opacity="0.25" />
                    <rect x="112" y="226" width="60" height="8" rx="4" fill="#0B1957" opacity="0.25" />
                    <rect x="112" y="244" width="40" height="8" rx="4" fill="#0B1957" opacity="0.25" />
                    <rect x="112" y="270" width="68" height="10" rx="5" fill="#ff6a00" />
                    <rect x="112" y="292" width="50" height="8" rx="4" fill="#0B1957" opacity="0.25" />
                    <rect x="112" y="310" width="56" height="8" rx="4" fill="#0B1957" opacity="0.25" />

                    {/* Main content KPI tiles */}
                    <rect x="208" y="172" width="140" height="74" rx="12" fill="#fff" stroke="rgba(11,25,87,0.08)" />
                    <rect x="222" y="186" width="60" height="8" rx="4" fill="#0B1957" opacity="0.4" />
                    <text x="222" y="222" fontFamily="Montserrat, sans-serif" fontWeight="700" fontSize="20" fill="#0B1957">+128%</text>
                    <rect x="298" y="216" width="40" height="14" rx="7" fill="#ff6a00" opacity="0.18" />
                    <text x="318" y="226" fontFamily="Montserrat, sans-serif" fontWeight="700" fontSize="9" fill="#d44a00" textAnchor="middle">ROI</text>

                    <rect x="358" y="172" width="140" height="74" rx="12" fill="#fff" stroke="rgba(11,25,87,0.08)" />
                    <rect x="372" y="186" width="60" height="8" rx="4" fill="#0B1957" opacity="0.4" />
                    <text x="372" y="222" fontFamily="Montserrat, sans-serif" fontWeight="700" fontSize="20" fill="#0B1957">99.9%</text>
                    <rect x="448" y="216" width="40" height="14" rx="7" fill="#1438bc" opacity="0.14" />
                    <text x="468" y="226" fontFamily="Montserrat, sans-serif" fontWeight="700" fontSize="9" fill="#1438bc" textAnchor="middle">UPTIME</text>

                    {/* Chart card */}
                    <rect x="208" y="262" width="290" height="148" rx="12" fill="#fff" stroke="rgba(11,25,87,0.08)" />
                    <rect x="222" y="276" width="80" height="8" rx="4" fill="#0B1957" opacity="0.55" />
                    <rect x="222" y="290" width="50" height="6" rx="3" fill="#0B1957" opacity="0.2" />

                    {/* Chart grid */}
                    <line x1="222" y1="380" x2="486" y2="380" stroke="#0B1957" strokeOpacity="0.07" />
                    <line x1="222" y1="356" x2="486" y2="356" stroke="#0B1957" strokeOpacity="0.07" />
                    <line x1="222" y1="332" x2="486" y2="332" stroke="#0B1957" strokeOpacity="0.07" />

                    {/* Animated chart line */}
                    <motion.path
                        d="M 226 376 L 270 348 L 314 360 L 358 322 L 402 338 L 446 308 L 484 320"
                        fill="none" stroke="url(#bt-grad-warm)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 1 }}
                        transition={{ duration: 2, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    />
                    {/* area fill */}
                    <motion.path
                        d="M 226 376 L 270 348 L 314 360 L 358 322 L 402 338 L 446 308 L 484 320 L 484 400 L 226 400 Z"
                        fill="url(#bt-grad-warm)" opacity="0.12"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.12 }}
                        transition={{ duration: 1, delay: 1.4 }}
                    />

                    {/* Dots */}
                    <motion.circle cx="446" cy="308" r="5" fill="#fff" stroke="#d44a00" strokeWidth="3"
                        animate={{ scale: [1, 1.25, 1] }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    />
                </motion.g>

                {/* Floating mini-card: AI badge */}
                <motion.g
                    filter="url(#bt-shadow-soft)"
                    initial={{ opacity: 0, x: -30, y: -10 }}
                    animate={{ opacity: 1, x: 0, y: [0, -8, 0] }}
                    transition={{
                        opacity: { duration: 0.6, delay: 1 },
                        x: { duration: 0.7, delay: 1, ease: [0.22, 1, 0.36, 1] },
                        y: { duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 1 },
                    }}
                >
                    <rect x="36" y="316" width="170" height="78" rx="16" fill="#fff" stroke="rgba(11,25,87,0.08)" />
                    <rect x="50" y="332" width="46" height="46" rx="12" fill="url(#bt-grad-warm)" />
                    <text x="73" y="362" fontFamily="Montserrat, sans-serif" fontWeight="800" fontSize="18" fill="#fff" textAnchor="middle">AI</text>
                    <text x="108" y="350" fontFamily="Montserrat, sans-serif" fontWeight="700" fontSize="13" fill="#0B1957">GPT Powered</text>
                    <text x="108" y="368" fontFamily="Montserrat, sans-serif" fontWeight="500" fontSize="10" fill="#5a6478">Smart automation</text>
                    <circle cx="186" cy="332" r="5" fill="#28c76f" />
                </motion.g>

                {/* Floating mini-card: Cloud secure */}
                <motion.g
                    filter="url(#bt-shadow-soft)"
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0, y: [0, 10, 0] }}
                    transition={{
                        opacity: { duration: 0.6, delay: 1.2 },
                        x: { duration: 0.7, delay: 1.2, ease: [0.22, 1, 0.36, 1] },
                        y: { duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1.2 },
                    }}
                >
                    <rect x="412" y="446" width="180" height="86" rx="16" fill="#fff" stroke="rgba(11,25,87,0.08)" />
                    <rect x="426" y="464" width="50" height="50" rx="14" fill="url(#bt-grad-navy)" />
                    {/* Shield icon */}
                    <path d="M 451 478 L 463 482 L 463 491 C 463 497 458 501 451 504 C 444 501 439 497 439 491 L 439 482 Z"
                        fill="#fff" opacity="0.95" />
                    <path d="M 446 491 L 450 495 L 458 487" fill="none" stroke="#ff6a00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <text x="486" y="482" fontFamily="Montserrat, sans-serif" fontWeight="700" fontSize="13" fill="#0B1957">Cloud Native</text>
                    <text x="486" y="500" fontFamily="Montserrat, sans-serif" fontWeight="500" fontSize="10" fill="#5a6478">SOC 2 · GDPR · HIPAA</text>
                    <rect x="486" y="510" width="60" height="14" rx="7" fill="#1438bc" opacity="0.1" />
                    <text x="516" y="520" fontFamily="Montserrat, sans-serif" fontWeight="700" fontSize="9" fill="#1438bc" textAnchor="middle">SECURE</text>
                </motion.g>

                {/* Floating sparks */}
                <motion.g
                    animate={{ rotate: [0, 360] }}
                    style={{ transformOrigin: '545px 80px' }}
                    transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
                >
                    <path d="M 545 60 L 549 76 L 565 80 L 549 84 L 545 100 L 541 84 L 525 80 L 541 76 Z"
                        fill="url(#bt-grad-warm)" />
                </motion.g>
                <motion.circle cx="48" cy="160" r="6" fill="#1438bc"
                    animate={{ y: [0, -10, 0], opacity: [0.6, 1, 0.6] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                />
                <motion.circle cx="585" cy="280" r="4" fill="#ff6a00"
                    animate={{ y: [0, 10, 0], opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
                />

                {/* Connecting line */}
                <line x1="120" y1="395" x2="200" y2="355" stroke="url(#bt-line)" strokeWidth="1.4" />
            </motion.svg>
        </motion.div>
    );
};

export default HeroVisual;
